// PushNotifications.js — client-side push notification setup
// Registers FCM token with Supabase and handles incoming notifications
//
// Usage in MainApp.jsx or App.js:
//   import { initPushNotifications } from "./PushNotifications";
//   useEffect(() => { if (user) initPushNotifications(supabase, user.id); }, [user]);

import { supabase } from "./supabase";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

// ── Register push token with Supabase ─────────────────────────────────────────
export async function initPushNotifications(supabaseClient, userId) {
  if (!userId) return;

  try {
    // Check if running in Capacitor (Android)
    if (window.Capacitor?.isNativePlatform()) {
      await registerCapacitorPush(supabaseClient, userId);
    } else if ("serviceWorker" in navigator && "PushManager" in window) {
      await registerWebPush(supabaseClient, userId);
    }
  } catch (err) {
    console.warn("Push notification setup failed:", err.message);
  }
}

// ── Capacitor Android push ────────────────────────────────────────────────────
async function registerCapacitorPush(supabaseClient, userId) {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== "granted") return;

    // Register with FCM
    await PushNotifications.register();

    // Get FCM token
    PushNotifications.addListener("registration", async (token) => {
      await saveToken(supabaseClient, userId, token.value, "android");
    });

    // Handle incoming notifications when app is open
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      // Show as in-app toast if app is open
      window.dispatchEvent(new CustomEvent("ojthub:notification", {
        detail: {
          title: notification.title,
          body: notification.body,
          data: notification.data,
        }
      }));
    });

    // Handle notification tap
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification.data;
      if (data?.link) window.location.href = data.link;
    });

  } catch (err) {
    console.warn("Capacitor push failed:", err.message);
  }
}

// ── Web push (browser) ────────────────────────────────────────────────────────
async function registerWebPush(supabaseClient, userId) {
  if (!VAPID_PUBLIC_KEY) return;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    await saveToken(supabaseClient, userId,
      JSON.stringify(subscription), "web");
  } catch (err) {
    console.warn("Web push failed:", err.message);
  }
}

// ── Save token to Supabase ────────────────────────────────────────────────────
async function saveToken(supabaseClient, userId, token, platform) {
  await supabaseClient.from("push_tokens").upsert({
    user_id:    userId,
    token,
    platform,
    is_active:  true,
    updated_at: new Date().toISOString(),
  }, {
    onConflict: "token",
  });
}

// ── Send push from client (via Edge Function) ─────────────────────────────────
export async function sendPushNotification(supabaseClient, {
  userId, title, body, data = {}, link = null
}) {
  try {
    await supabaseClient.functions.invoke("send-push", {
      body: { userId, title, body, data: { ...data, link } },
    });
  } catch (err) {
    console.warn("Push send failed:", err.message);
  }
}

// ── Notification event shortcuts ──────────────────────────────────────────────
export const PushEvents = {
  // Wire these into your supabase operations:

  onEndorsementApproved: (userId) =>
    sendPushNotification(supabase, {
      userId,
      title: "Endorsement Approved! ✅",
      body: "Your OJT coordinator has approved your endorsement. You can now start your OJT.",
      data: { type: "endorsement" },
      link: "/app",
    }),

  onLogbookApproved: (userId, hours) =>
    sendPushNotification(supabase, {
      userId,
      title: "Logbook Entry Approved",
      body: `${hours} hours have been approved by your coordinator.`,
      data: { type: "logbook" },
      link: "/logbook",
    }),

  onApplicationUpdate: (userId, jobTitle, status) =>
    sendPushNotification(supabase, {
      userId,
      title: status === "accepted"
        ? "Application Accepted! 🎉"
        : `Application Update: ${status}`,
      body: `Your application for ${jobTitle} has been ${status}.`,
      data: { type: "application", status },
      link: "/app",
    }),

  onNewApplicant: (userId, studentName, jobTitle) =>
    sendPushNotification(supabase, {
      userId,
      title: "New Application Received 📨",
      body: `${studentName} applied for ${jobTitle}`,
      data: { type: "applicant" },
      link: "/employer",
    }),

  onCertificateReady: (userId, certNumber) =>
    sendPushNotification(supabase, {
      userId,
      title: "Your Certificate is Ready! 🎓",
      body: "Your OJT Certificate of Completion is ready to download.",
      data: { type: "certificate", certNumber },
      link: "/certificate",
    }),
};

// ── Helper ────────────────────────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw     = window.atob(base64);
  const output  = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}
